import { createFileRoute, Link } from "@tanstack/react-router";
import {
	ArrowLeft,
	Building2,
	Calendar,
	Download,
	FileText,
	Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { getStatusColor, getStatusLabel, getEffectiveStatus } from "@/lib/utils";

// Local types
interface PublicSolicitation {
	id: string;
	title: string;
	description: string;
	type: string;
	status: string;
	dueDate: string | null;
	publishDate: string | null;
}

interface PublicBid {
	id: string;
	vendorName: string;
	score: number | null;
	status: string;
}

export const Route = createFileRoute("/public/solicitations/$id/results")({
	component: PublicBidResultsPage,
});

function PublicBidResultsPage() {
	const { id } = Route.useParams();
	// Public data would come from a public tRPC endpoint
	const solicitation: PublicSolicitation | null = null;
	const solicitationBids: PublicBid[] = [];

	if (!solicitation) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<Card className="w-full max-w-md">
					<CardContent className="pt-6 text-center">
						<h2 className="text-xl font-bold mb-2">Solicitation Not Found</h2>
						<Button asChild>
							<Link to="/public">Back to Solicitations</Link>
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Sort bids by score (highest first)
	const rankedBids = [...solicitationBids]
		.filter((b) => b.score !== null)
		.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));

	const bidOpeningDate = solicitation.dueDate;

	return (
		<div className="min-h-screen bg-gray-50">
			{/* Header */}
			<header className="bg-white border-b">
				<div className="max-w-5xl mx-auto px-4 py-4 sm:px-6">
					<div className="flex items-center gap-3">
						<Button variant="ghost" size="icon" asChild>
							<Link to="/public/solicitations/$id" params={{ id }}>
								<ArrowLeft className="h-4 w-4" />
							</Link>
						</Button>
						<div>
							<h1 className="font-semibold text-lg">Bid Results</h1>
							<p className="text-sm text-muted-foreground">
								{solicitation.title}
							</p>
						</div>
					</div>
				</div>
			</header>

			<main className="max-w-5xl mx-auto px-4 py-8 sm:px-6 space-y-6">
				{/* Summary Card */}
				<Card>
					<CardHeader>
						<div className="flex items-center justify-between">
							<div>
								<CardTitle>{solicitation.title}</CardTitle>
								<CardDescription>
									<span>{solicitation.type}</span>
									<span> - {solicitation.id}</span>
								</CardDescription>
							</div>
							<Badge className={getStatusColor(getEffectiveStatus(solicitation.status, solicitation.publishDate, solicitation.dueDate))}>
								{getStatusLabel(getEffectiveStatus(solicitation.status, solicitation.publishDate, solicitation.dueDate))}
							</Badge>
						</div>
					</CardHeader>
					<CardContent>
						<div className="grid gap-4 md:grid-cols-3">
							<div className="flex items-center gap-3">
								<Calendar className="h-5 w-5 text-muted-foreground" />
								<div>
									<p className="text-sm text-muted-foreground">
										Bid Opening Date
									</p>
									<p className="font-medium">
										{new Date(bidOpeningDate).toLocaleString()}
									</p>
								</div>
							</div>
							<div className="flex items-center gap-3">
								<Building2 className="h-5 w-5 text-muted-foreground" />
								<div>
									<p className="text-sm text-muted-foreground">
										Total Bids Received
									</p>
									<p className="font-medium">{solicitationBids.length}</p>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>

				{/* Bid Tabulation */}
				<Card>
					<CardHeader className="flex flex-row items-center justify-between">
						<div>
							<CardTitle>Bid Tabulation</CardTitle>
							<CardDescription>
								Public bid results sorted by score
							</CardDescription>
						</div>
						<div className="flex gap-2">
							<Button
								variant="outline"
								onClick={() => toast.success("Exporting as PDF...")}
							>
								<Download className="mr-2 h-4 w-4" />
								PDF
							</Button>
							<Button
								variant="outline"
								onClick={() => toast.success("Exporting as CSV...")}
							>
								<Download className="mr-2 h-4 w-4" />
								CSV
							</Button>
						</div>
					</CardHeader>
					<CardContent>
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead className="w-16">Rank</TableHead>
									<TableHead>Vendor</TableHead>
									<TableHead>Score</TableHead>
									<TableHead>Status</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{rankedBids.map((bid, index) => (
									<TableRow
										key={bid.id}
										className={index === 0 ? "bg-green-50" : ""}
									>
										<TableCell>
											<div className="flex items-center gap-2">
												{index === 0 && (
													<Trophy className="h-4 w-4 text-yellow-500" />
												)}
												<span className={index === 0 ? "font-bold" : ""}>
													#{index + 1}
												</span>
											</div>
										</TableCell>
										<TableCell className="font-medium">
											{bid.vendorName}
										</TableCell>
										<TableCell>
											<span
												className={
													index === 0 ? "font-bold text-green-700" : ""
												}
											>
												{bid.score}/100
											</span>
										</TableCell>
										<TableCell>
											<Badge
												variant={
													bid.status === "awarded" ? "default" : "outline"
												}
											>
												{bid.status === "awarded"
													? "Awarded"
													: bid.status.replace("_", " ")}
											</Badge>
										</TableCell>
									</TableRow>
								))}
								{rankedBids.length === 0 && (
									<TableRow>
										<TableCell
											colSpan={4}
											className="text-center py-8 text-muted-foreground"
										>
											Bid results are not yet available.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</CardContent>
				</Card>

				{/* Disclaimer */}
				<Card>
					<CardContent className="pt-6">
						<div className="flex items-start gap-3">
							<FileText className="h-5 w-5 text-muted-foreground mt-0.5" />
							<div className="text-sm text-muted-foreground">
								<p className="font-medium text-foreground">Disclaimer</p>
								<p className="mt-1">
									This bid tabulation is provided for informational purposes
									only. The final award decision is at the sole discretion of
									the awarding authority. Scores and rankings are based on the
									evaluation criteria specified in the solicitation.
								</p>
							</div>
						</div>
					</CardContent>
				</Card>
			</main>
		</div>
	);
}
